import { useCallback, useState } from 'react';
import { strategySimulationApi } from '../../services/api';
import type {
    StrategySimulationRunResponse,
    StrategySimulationWeightProfile,
} from '../../types';
import {
    subscribeStrategySimulationRun,
    StrategySimulationGatewayTimeout,
    StrategySimulationPollTimeout,
} from './subscribeStrategySimulationRun';
const ALL_WEIGHT_PROFILES: StrategySimulationWeightProfile[] = [
    'traffic_heavy',
    'balanced',
    'category_heavy',
    'similarity_heavy',
];
const STRATEGY_ORDER: Record<string, number> = {
    embedding_default: 0,
    hard_gate: 1,
    soft_boost: 2,
    default_tiered: 3,
    default_legacy: 4,
};
const PROFILE_ORDER: Record<StrategySimulationWeightProfile, number> = {
    traffic_heavy: 0,
    balanced: 1,
    category_heavy: 2,
    similarity_heavy: 3,
};

function validateSampleAndHorizon(sampleSize: number, horizonDays: number): string | null {
    if (sampleSize < 200 || sampleSize > 5000) {
        return 'Sample size must be between 200 and 5000.';
    }
    if (horizonDays < 1 || horizonDays > 7) {
        return 'Traffic horizon must be between 1 and 7 days.';
    }
    return null;
}

function getHttpErrorPayload(e: unknown): { status?: number; error?: unknown } {
    if (typeof e !== 'object' || e === null || !('response' in e)) {
        return {};
    }
    const res = (e as { response?: { status?: number; data?: { error?: unknown } } }).response;
    if (!res) {
        return {};
    }
    return { status: res.status, error: res.data?.error };
}

function resolveRunErrorMessage(e: unknown): string {
    if (e instanceof StrategySimulationPollTimeout || e instanceof StrategySimulationGatewayTimeout) {
        return 'The simulation timed out. Some strategies may show timeout status.';
    }
    const { status: st, error: errField } = getHttpErrorPayload(e);
    if (st === 429) {
        return 'Too many simulations running. Try again shortly.';
    }
    if (st === 403 || st === 404) {
        return 'Simulator unavailable.';
    }
    const fromApi = messageFromApiErrorField(errField);
    if (fromApi) {
        return fromApi;
    }
    return 'The simulation could not be completed. Try again.';
}

function messageFromApiErrorField(error: unknown): string | null {
    if (typeof error === 'string' && error.trim()) {
        return error.trim();
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const m = (error as { message: unknown }).message;
        if (typeof m === 'string' && m.trim()) {
            return m.trim();
        }
    }
    return null;
}

function computeOverallPercent(
    urlProgress:
        | { urlsScanned: number; urlsTarget: number; inDeterministicStage: boolean }
        | null,
): number {
    if (!urlProgress || urlProgress.urlsTarget <= 0) return 0;
    const fraction = Math.min(1, urlProgress.urlsScanned / urlProgress.urlsTarget);
    // Deterministic (URL scan) stage fills only the first half of the bar;
    // the remaining 50–100% is reserved for downstream scoring. Legacy runs
    // (without deterministicVersion) keep the full 0–100% mapping.
    const scale = urlProgress.inDeterministicStage ? 50 : 100;
    return fraction * scale;
}

function tagFullRunProfile(
    run: StrategySimulationRunResponse,
    profile: StrategySimulationWeightProfile,
): StrategySimulationRunResponse {
    if (!run.results) return run;
    return {
        ...run,
        results: run.results.map((row) =>
            row.strategy === 'soft_boost'
                ? { ...row, profile }
                : { ...row, profile: undefined },
        ),
    };
}

function takeSoftBoostOnly(
    run: StrategySimulationRunResponse,
    profile: StrategySimulationWeightProfile,
): StrategySimulationRunResponse {
    const soft = run.results?.find((r) => r.strategy === 'soft_boost');
    if (!soft) {
        return { ...run, results: [], recommended: null };
    }
    return {
        ...run,
        results: [{ ...soft, profile }],
        recommended: null,
    };
}

function sortMergedRows(
    a: NonNullable<StrategySimulationRunResponse['results']>[number],
    b: NonNullable<StrategySimulationRunResponse['results']>[number],
): number {
    const strategyDiff = (STRATEGY_ORDER[a.strategy] ?? 99) - (STRATEGY_ORDER[b.strategy] ?? 99);
    if (strategyDiff !== 0) return strategyDiff;
    const aProfile = a.profile ?? 'traffic_heavy';
    const bProfile = b.profile ?? 'traffic_heavy';
    return PROFILE_ORDER[aProfile] - PROFILE_ORDER[bProfile];
}

function pickTopRecommended(rows: NonNullable<StrategySimulationRunResponse['results']>) {
    const eligible = rows.filter((row) => row.eligible);
    if (eligible.length === 0) return null;
    eligible.sort((a, b) => b.score - a.score);
    const top = eligible[0];
    return {
        strategy: top.strategy,
        profile: top.profile,
        score: top.score,
        eligible: true,
    };
}

function mergeRuns(runs: StrategySimulationRunResponse[]): StrategySimulationRunResponse {
    const [head, ...tail] = runs;
    const headRows = head?.results ?? [];
    const tailRows = tail.flatMap((r) => r.results ?? []);
    const allRows = [...headRows, ...tailRows].sort(sortMergedRows);
    const recommended = head?.recommended ?? pickTopRecommended(allRows);
    return {
        runId: `combo_${Date.now()}`,
        status: 'completed',
        mode: 'read_only',
        deterministicVersion: runs[0]?.deterministicVersion,
        recommended,
        results: allRows,
    };
}

async function pollRun(
    promptId: string,
    runId: string,
    onPoll?: (data: StrategySimulationRunResponse) => void,
): Promise<StrategySimulationRunResponse> {
    return subscribeStrategySimulationRun(promptId, runId, { onPoll });
}

async function runPrimarySimulation(
    promptId: string,
    sampleSize: number,
    horizonDays: number,
    countryCodes: string[],
    excludeLast24Hours: boolean,
    profile: StrategySimulationWeightProfile,
    onPoll?: (data: StrategySimulationRunResponse) => void,
): Promise<StrategySimulationRunResponse> {
    const created = await strategySimulationApi.create(promptId, {
        sampleSize,
        horizonDays,
        countryCodes: countryCodes.length ? countryCodes.map((c) => c.toLowerCase()) : undefined,
        weightProfile: profile,
        excludeLast24Hours,
        persistResult: false,
    });
    const finalRun = await pollRun(promptId, created.runId, onPoll);
    return tagFullRunProfile(finalRun, profile);
}

async function runAllProfileSimulations(
    promptId: string,
    sampleSize: number,
    horizonDays: number,
    countryCodes: string[],
    excludeLast24Hours: boolean,
    profiles: StrategySimulationWeightProfile[],
    callbacks: {
        onProfileStart: (profile: StrategySimulationWeightProfile) => void;
        onProfileComplete: (completedCount: number) => void;
        onPoll?: (data: StrategySimulationRunResponse) => void;
    },
): Promise<StrategySimulationRunResponse> {
    const [primaryProfile] = profiles;
    const onPoll = callbacks.onPoll;
    callbacks.onProfileStart(primaryProfile);
    const created = await strategySimulationApi.create(promptId, {
        sampleSize,
        horizonDays,
        countryCodes: countryCodes.length ? countryCodes.map((c) => c.toLowerCase()) : undefined,
        weightProfiles: profiles,
        excludeLast24Hours,
        persistResult: false,
    });
    const finalRun = await pollRun(promptId, created.runId, onPoll);
    callbacks.onProfileComplete(1);
    return finalRun;
}

export function useStrategySimulatorPanelState(promptId: string, initialCountryCodes: string[]) {
    const [sampleSize, setSampleSize] = useState(1000);
    const [horizonDays, setHorizonDays] = useState(7);
    const [countryCodes, setCountryCodes] = useState<string[]>(initialCountryCodes);
    const [excludeLast24Hours, setExcludeLast24Hours] = useState(true);
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [runError, setRunError] = useState<string | null>(null);
    const [polling, setPolling] = useState(false);
    const [runResult, setRunResult] = useState<StrategySimulationRunResponse | null>(null);
    const [completedProfiles, setCompletedProfiles] = useState(0);
    const [activeProfile, setActiveProfile] = useState<StrategySimulationWeightProfile | null>(null);
    const [urlScanProgress, setUrlScanProgress] = useState<{
        urlsScanned: number;
        urlsTarget: number;
        inDeterministicStage: boolean;
    } | null>(null);

    const runSimulation = useCallback(async () => {
        const err = validateSampleAndHorizon(sampleSize, horizonDays);
        if (err) {
            setFieldError(err);
            return;
        }
        setFieldError(null);
        setRunError(null);
        setPolling(true);
        setRunResult(null);
        setCompletedProfiles(0);
        setActiveProfile(null);
        setUrlScanProgress(null);
        try {
            const combined = await runAllProfileSimulations(
                promptId,
                sampleSize,
                horizonDays,
                countryCodes,
                excludeLast24Hours,
                ALL_WEIGHT_PROFILES,
                {
                    onProfileStart: (profile) => {
                        setActiveProfile(profile);
                    },
                    onProfileComplete: (count) => {
                        setCompletedProfiles(count);
                        setUrlScanProgress(null);
                    },
                    onPoll: (data) => {
                        if (data.progress) {
                            setUrlScanProgress({
                                urlsScanned: data.progress.urlsScanned,
                                urlsTarget: data.progress.urlsTarget,
                                inDeterministicStage: Boolean(data.deterministicVersion),
                            });
                        }
                    },
                },
            );
            setRunResult(combined);
        } catch (e) {
            setRunError(resolveRunErrorMessage(e));
        } finally {
            setActiveProfile(null);
            setPolling(false);
            setUrlScanProgress(null);
        }
    }, [countryCodes, excludeLast24Hours, horizonDays, promptId, sampleSize]);

    const totalProfiles = 1;

    return {
        sampleSize,
        setSampleSize,
        horizonDays,
        setHorizonDays,
        countryCodes,
        setCountryCodes,
        excludeLast24Hours,
        setExcludeLast24Hours,
        fieldError,
        runError,
        polling,
        runResult,
        completedProfiles,
        totalProfiles,
        activeProfile,
        urlScanProgress,
        overallPercent: polling ? computeOverallPercent(urlScanProgress) : 0,
        runSimulation,
    };
}
